/**
 * entities/notification/api/useNotifications — Typed query hook for notification list.
 *
 * Wraps GET /api/notifications/ with TanStack Query.
 * Only polls when the user is authenticated (enabled flag driven by sessionStore).
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';
import { useSessionStore } from '@/entities/session';
import type { NotificationList } from '../model/types';

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

/** Polling interval for the notifications list (30 seconds). */
const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

/**
 * Returns the full list of notifications for the current user.
 * Polling is disabled when the user is not authenticated.
 */
export function useNotificationsQuery() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);

  return useQuery<NotificationList, Error>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: async (): Promise<NotificationList> => {
      const result = await (
        apiClient.GET as (
          p: string,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/notifications/');

      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }

      // The endpoint returns the standard pagination envelope; the hook
      // exposes the current page's items (pagination UI reads the envelope
      // via its own query in the notifications page).
      const data = result.data as { results?: NotificationList } | undefined;
      return data?.results ?? [];
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? NOTIFICATIONS_POLL_INTERVAL_MS : false,
    staleTime: 10_000,
  });
}
